import type { State } from "@shared/types/graphqlGenerated";
type AddressComponent = google.maps.GeocoderAddressComponent;

interface GoogleAddress {
  streetAddress: string;
  city: string;
  region: string;
  postalCode: string;
}

export const selectState = (stateAddr: string, states: State[]) => {
  const foundState = states.find(
    (state: State) => stateAddr.toUpperCase() === state.abbr.toUpperCase()
  );

  return foundState ? foundState.abbr : null;
};

const isStreetNumberType = ({ types }: AddressComponent) => types.includes("street_number");
export const getStreetNumber = (components: AddressComponent[]): string => {
  const streetNumber = components.find(isStreetNumberType);
  if (streetNumber) {
    return streetNumber.long_name;
  }

  return null;
};

const isRouteType = ({ types }: AddressComponent) => types.includes("route");
export const getAddress = (components: AddressComponent[]): string => {
  const route = components.find(isRouteType);
  if (!route) {
    return "";
  }

  const streetNumber = getStreetNumber(components);
  if (streetNumber) {
    return `${streetNumber} ${route.long_name}`.trim();
  }

  return `${route.long_name}`.trim();
};

export const isCityType = ({ types }: AddressComponent) => {
  return (
    types.includes("locality") ||
    types.includes("sublocality") ||
    types.includes("administrative_area_level_3")
  );
};
export const getCity = (components: AddressComponent[]): string => {
  const cityComponent = components.find(isCityType);
  if (!cityComponent) {
    return null;
  }

  return cityComponent.long_name;
};

const isStateType = ({ types }: AddressComponent) => types.includes("administrative_area_level_1");
export const getRegion = (components: AddressComponent[], states): string => {
  const stateComponent = components.find(isStateType);
  if (!stateComponent) {
    return null;
  }

  return selectState(stateComponent.short_name, states);
};

const isZipType = ({ types }: AddressComponent) => types.includes("postal_code");
export const getPostalCode = (components: AddressComponent[]): string => {
  const zipComponent = components.find(isZipType);
  if (!zipComponent) {
    return null;
  }

  return zipComponent.long_name;
};

const GoogleAddress = (
  placeDetails: google.maps.GeocoderResult[],
  selectedStreetNumber: string,
  states: State[]
): GoogleAddress => {
  const addressComponents = placeDetails[0].address_components;

  const streetAddress = getAddress(addressComponents) || selectedStreetNumber;
  const city = getCity(addressComponents);
  const region = getRegion(addressComponents, states);
  const postalCode = getPostalCode(addressComponents);

  return { streetAddress, city, region, postalCode };
};

export { GoogleAddress };
